import React, { useMemo, useLayoutEffect, useEffect, useState, useRef } from "react";
import moment from "moment";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { OverlayTrigger, Tooltip as BsTooltip } from "react-bootstrap";
import { PageHeading } from "../../components";
import getPressureOverview from "../../services/apis/get-pressure-overview";
import PressureHeatMap from "./pressure-heat-map";

const POTABLE_COLOR = "#09A3B3";
const RECYCLED_COLOR = "#f0a500";

const formatDayTime = (value) => {
  if (!value) return "";
  const iso = moment(value);
  const m = iso.isValid() ? iso : moment(value, "DD-MM-YYYY HH:mm:ss");
  return m.isValid() ? m.format("dddd, h:mma") : value;
};

const summaryRows = [
  ["Number of Sites", (d) => d.siteId],
  ["Avg pressure", (d) => `${d.avgPressure} m head`],
  ["Min pressure (sites with flow > 0)", (d) => `${d.minPressure} m head`],
  ["Min site_id", (d) => d.minSiteId, { site: true }],
  ["Max pressure", (d) => `${d.maxPressure} m head`],
  ["Max site_id", (d) => d.maxSiteId, { site: true }],
  ["Std deviation", (d) => d.stdDeviation],
  ["3σ outliers (count instances)", (d) => d.outliers3Sigma],
  ["Zero flow sites", (d) => d.zeroFlowSites],
  ["Pressure transient", (d) => d.pressureTransient],
  ["Fluctuation in pressure", (d) => d.pressureFluctuation],
  ["Day time of highest pressure", (d) => formatDayTime(d.dayTimeHighestPressure)],
  ["Day time of lowest pressure", (d) => formatDayTime(d.dayTimeLowestPressure)],
];

// Hover tooltip text explaining the meaning of each summary row.
const ROW_DESCRIPTIONS = {
  "Number of Sites": "Total number of monitored pressure sites included in this summary.",
  "Avg pressure": "Average effective pressure across all sites (m head).",
  "Min pressure (sites with flow > 0)":
    "Lowest pressure among sites that currently have flow greater than zero (m head).",
  "Min site_id": "Site with the minimum pressure. Click the value to locate it on the map.",
  "Max pressure": "Highest pressure recorded across all sites (m head).",
  "Max site_id": "Site with the maximum pressure. Click the value to locate it on the map.",
  "Std deviation":
    "Standard deviation of pressure across sites — how spread out the values are.",
  "3σ outliers (count instances)":
    "Number of readings beyond 3 standard deviations from the mean (statistical outliers).",
  "Zero flow sites": "Number of sites currently reporting zero flow.",
  "Pressure transient": "Count of short, sudden pressure spikes or drops (transients).",
  "Fluctuation in pressure": "Degree of variability / fluctuation in pressure over the period.",
  "Day time of highest pressure": "Day and time when the highest pressure occurred.",
  "Day time of lowest pressure": "Day and time when the lowest pressure occurred.",
};

const renderFrequencyTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      style={{
        backgroundColor: "#fff",
        padding: "8px 12px",
        borderRadius: 6,
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label} m head</div>
      <div style={{ color: POTABLE_COLOR }}>Count: {payload[0].value}</div>
    </div>
  );
};

const renderWaterTypeTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0]?.payload;
  return (
    <div
      style={{
        backgroundColor: "#fff",
        padding: "8px 12px",
        borderRadius: 6,
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{point?.displayDate}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.stroke, fontSize: 13 }}>
          {p.name}: {p.value} m head
        </div>
      ))}
    </div>
  );
};

const PressurePage = () => {
  const [summary, setSummary] = useState(null);
  const [frequencyData, setFrequencyData] = useState([]);
  const [waterTypeData, setWaterTypeData] = useState([]);
  const [heatMap, setHeatMap] = useState(null);
  const heatMapRef = useRef(null);

  const focusSite = (siteId) => heatMapRef.current?.focusSite(siteId);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useLayoutEffect(() => {
    import("./style.scss");
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await getPressureOverview();
        if (cancelled) return;
        setSummary(data?.summary ?? null);
        setFrequencyData(Array.isArray(data?.frequency) ? data.frequency : []);
        setWaterTypeData(Array.isArray(data?.waterType) ? data.waterType : []);
        setHeatMap(data?.heatMap ?? null);
      } catch (err) {
        if (cancelled) return;
        setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, []);

  const waterTypeTicks = useMemo(() => {
    const seen = new Set();
    return waterTypeData
      .filter((d) => {
        const dayKey = moment(d.timestamp).format("YYYY-MM-DD");
        if (!seen.has(dayKey)) {
          seen.add(dayKey);
          return true;
        }
        return false;
      })
      .map((d) => d.timestamp);
  }, [waterTypeData]);

  const tickFormatter = (ts) => moment(ts).format("ddd DD MMM");

  return (
    <div className="the-content">
      <PageHeading title="Pressure" />
      <div className="container-fluid content">

        {loading && (
          <div className="row mt-4">
            <div className="col-12">
              <div className="pressure-card">
                <div className="pressure-card__body">Loading…</div>
              </div>
            </div>
          </div>
        )}

        {error && !loading && (
          <div className="row mt-4">
            <div className="col-12">
              <div className="pressure-card">
                <div className="pressure-card__body" style={{ color: "#c0392b" }}>
                  Failed to load pressure data.
                </div>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Summary data (40%) + Pressure heat map (60%) */}
            <div className="row mt-4">
              {summary && (
                <div className="col-12 col-lg-5 mb-4 mb-lg-0">
                  <div className="pressure-card">
                    <div className="pressure-card__header">
                      Summary data — all sites (last 7 days)
                    </div>
                    <div className="table-responsive">
                      <table className="table pressure-summary-table">
                        <thead>
                          <tr>
                            <th>Metric</th>
                            <th>Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {summaryRows.map(([label, getter, opts], i) => {
                            const value = getter(summary);
                            const isSiteLink =
                              opts?.site && value != null && value !== "";
                            const desc = ROW_DESCRIPTIONS[label];
                            const row = (
                              <tr>
                                <td className="pressure-summary-table__label">{label}</td>
                                <td>
                                  {isSiteLink ? (
                                    <button
                                      type="button"
                                      className="pressure-site-link"
                                      onClick={() => focusSite(value)}
                                      title="Show this site on the map"
                                    >
                                      {value}
                                    </button>
                                  ) : (
                                    value
                                  )}
                                </td>
                              </tr>
                            );
                            return desc ? (
                              <OverlayTrigger
                                key={label}
                                placement="top"
                                delay={{ show: 100, hide: 0 }}
                                overlay={
                                  <BsTooltip id={`pressure-tip-${i}`}>{desc}</BsTooltip>
                                }
                              >
                                {row}
                              </OverlayTrigger>
                            ) : (
                              React.cloneElement(row, { key: label })
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              <div
                className={
                  (summary ? "col-12 col-lg-7" : "col-12") + " pressure-map-col"
                }
              >
                <div className="pressure-card">
                  <div className="pressure-card__header">
                    Pressure heat map — all sites (last 7 days)
                  </div>
                  <div className="pressure-card__body">
                    <PressureHeatMap ref={heatMapRef} data={heatMap} />
                  </div>
                </div>
              </div>
            </div>

            {/* Figure 1: Frequency of pressure readings */}
            <div className="row mt-4">
              <div className="col-12">
                <div className="pressure-card">
                  <div className="pressure-card__header">
                    Frequency of pressure readings — all sites (last 7 days)
                  </div>
                  <div className="pressure-card__body">
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={frequencyData}
                        margin={{ top: 10, right: 20, left: 10, bottom: 30 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis
                          dataKey="pressure"
                          label={{
                            value: "Pressure (m head)",
                            position: "insideBottom",
                            offset: -20,
                            style: { fontSize: 12 },
                          }}
                          tick={{ fontSize: 11 }}
                        />
                        <YAxis
                          label={{
                            value: "Frequency count",
                            angle: -90,
                            position: "insideLeft",
                            offset: 10,
                            style: { fontSize: 12 },
                          }}
                          tick={{ fontSize: 11 }}
                        />
                        <Tooltip content={renderFrequencyTooltip} />
                        <Bar dataKey="count" fill={POTABLE_COLOR} radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>

            {/* Figure 4: Potable vs Recycled water pressure */}
            <div className="row mt-4 mb-5">
              <div className="col-12">
                <div className="pressure-card">
                  <div className="pressure-card__header">
                    Aggregated potable water vs recycled water pressure (last 7 days)
                  </div>
                  <div className="pressure-card__body">
                    <ResponsiveContainer width="100%" height={320}>
                      <LineChart
                        data={waterTypeData}
                        margin={{ top: 10, right: 20, left: 10, bottom: 10 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="timestamp"
                          type="number"
                          scale="time"
                          domain={["dataMin", "dataMax"]}
                          ticks={waterTypeTicks}
                          tickFormatter={tickFormatter}
                          tick={{ fontSize: 11 }}
                        />
                        <YAxis
                          label={{
                            value: "Pressure (m head)",
                            angle: -90,
                            position: "insideLeft",
                            offset: 10,
                            style: { fontSize: 12 },
                          }}
                          tick={{ fontSize: 11 }}
                          domain={[0, "auto"]}
                        />
                        <Tooltip content={renderWaterTypeTooltip} />
                        <Legend verticalAlign="top" />
                        <Line
                          type="monotone"
                          dataKey="potable"
                          name="Potable water"
                          stroke={POTABLE_COLOR}
                          strokeWidth={1.5}
                          dot={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="recycled"
                          name="Recycled water"
                          stroke={RECYCLED_COLOR}
                          strokeWidth={1.5}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default PressurePage;
